import React, { useEffect, useState } from "react";
import ItemRequestForm from "../ItemRequestForm";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Layout from "components/sales/layout/layout";

const EditBusinessItem = ({
  getBusinessItemList,
  getDropdownData,
  updateItemRequest,
  dropdownData,
  businessItemData,
  fetchBrandAndHsn,
  brandAndHsnData,
}) => {
  const { id, type } = useParams();
  const [formData, setFormData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // ======================
  // 🔹 Load dropdowns + item details
  // ======================
  useEffect(() => {
    const loadAll = async () => {
      try {
        setIsLoading(true);

        await getDropdownData();

        const res = await getBusinessItemList(null, type, 1, id, 1);
        const data = res;

        if (!data) {
          toast.error("Failed to load item details");
          return;
        }

        let meta = data?.meta_details;
        const isService = !('item_type' in data);

        const normalized = {
          id: data.id,
          name: isService ? data.service_name : data.material_name,
          description: data.description || meta.description || "",
          base_unit_of_measure: data.uom || "",
          gst_group_code: data.gst_id || "",
          revenue_type: (isService ? data.revenue_type : meta.revenue_type) || "",
          class_of_material: (isService ? parseInt(meta.class_of_material) : data.material_class_id) || "",
          type: isService ? meta.type : data.item_type,
          qc_enable: meta.qc_enable,
          qr_code: meta.qr_code,
          unit_cost: (isService ? data.base_price : data.cost_price) || "",
          unit_volume: meta.unit_volume || data.unit_volume || "",
          tag: (isService ? meta.tag : data.is_tag),
          net_weight: meta.net_weight || data.net_weight || "",
          gross_weight: meta.gross_weight || data.gross_weight || "",
          unit_price: meta.unit_price || data.unit_price || "",
          gen_prod_posting_group:
            meta.gen_prod_posting_group || data.gen_prod_posting_group || "",
          inventory_posting_group:
            meta.inventory_posting_group || data.inventory_posting_group || "",
          item_tracking_code:
            meta.item_tracking_code || data.item_tracking_code || "",
          serial_no: meta.serial_no || data.serial_no || "",
          brand: (isService ? meta.brand : data.brand_id) || "",
          item_category_code:
            (isService ? data.service_category_id : data.category_id) || "",
          hsn_sac_code: data.hsn_id || "",
        };

        setFormData(normalized);
        setIsInitialLoad(false);
      } catch (error) {
        console.error("Error loading data:", error);
        toast.error("Failed to load item details");
      } finally {
        setIsLoading(false);
      }
    };

    loadAll();
  }, [id, type]);

  // ======================
  // 🔹 Handle input changes
  // ======================
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value?.id ?? value ?? "",
    }));
  };

  // ======================
  // 🔹 Fetch HSN only after formData is set
  // ======================
  useEffect(() => {
    const fetchHsn = async () => {
      if (!formData?.gst_group_code) return;

      if (!isInitialLoad) {
        setFormData(prev => ({ ...prev, hsn_sac_code: null }));
      }

      try {
        setIsLoading(true);
        await fetchBrandAndHsn("gst", formData.gst_group_code, null);
      } catch (error) {
        console.error("Error fetching HSN:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (formData && dropdownData?.gst?.length) {
      fetchHsn();
    }
  }, [formData?.gst_group_code, dropdownData]);

  // ======================
  // 🔹 Fetch Brand on Category change
  // ======================
  useEffect(() => {
    if (!formData?.item_category_code) return;

    try {
      setIsLoading(true);

      if (formData.item_category_code === 9) {
        setFormData((prev) => ({ ...prev, tag: 1 }));
      } else {
        setFormData((prev) => ({ ...prev, tag: 0 }));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [formData?.item_category_code]);

  // ======================
  // 🔹 Auto-set serial no for QR
  // ======================
  useEffect(() => {
    if (formData?.item_tracking_code === "QR") {
      setFormData((prev) => ({
        ...prev,
        serial_no: "QR SERIAL",
      }));
    }
  }, [formData?.item_tracking_code]);

  useEffect(() => {
    if (!formData || !formData.type) return;
    setFormData((prev) => ({
      ...prev,
      inventory_posting_group: "",
    }));
  }, [formData?.type]);

  useEffect(() => {
    if (!formData || !formData.serial_no) return;
    setFormData((prev) => ({
      ...prev,
      qr_code: 1,
    }));
  }, [formData?.serial_no]);

  // ======================
  // 🔹 Submit
  // ======================
  const handleSubmit = async () => {
    try {

      if (!formData?.name || !formData?.base_unit_of_measure || !formData?.unit_cost
        || !formData?.class_of_material || !formData?.type || !formData?.gst_group_code || !formData?.hsn_sac_code || !formData?.remarks || (formData?.type != 'Service' && (!formData?.brand || !formData?.item_category_code))) {
        toast.error("Please fill all mandatory fields");
        return;
      }

      if ((formData?.type != 'Service' && formData?.class_of_material == 4) || (formData?.type == 'Service' && formData?.class_of_material != 4)) {
        toast.error("Invalid Type and Class of Material combination");
        return;
      }

      const payload = {
        ...formData,
      };

      payload.is_edit_request = 1;
      payload.id = id;

      setIsLoading(true);

      const res = await updateItemRequest(payload);
      if (res?.data?.status === true) {
        toast.success(res?.data?.message || "Item updated successfully!");
        window.history.back();
      } else {
        toast.error(res?.data?.message || "Failed to update request");
      }
    } catch (error) {
      console.error("Error updating item:", error);
      toast.error("Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  // ======================
  // 🔹 Render
  // ======================
  return (
    <>
      <Layout title={'Edit Item'} isLoading={isLoading} isGoback={true}>
        {formData &&
          <ItemRequestForm
            formData={formData}
            setFormData={setFormData}
            handleInputChange={handleInputChange}
            handleSubmit={handleSubmit}
            dropdownData={dropdownData}
            brandAndHsnData={brandAndHsnData}
            isLoading={isLoading}
            isEdit={true}
          />
        }
      </Layout>
    </>
  );
};

export default EditBusinessItem;
